import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, Package, Search } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { inventoryApi, fmtQty } from './inventoryApi';
import { RecipeDialog } from './RecipeDialog';

const STATUS = {
  out: { label: 'AGOTADO', cls: 'bg-red-900/50 text-red-300' },
  low: { label: 'BAJO', cls: 'bg-amber-900/50 text-amber-300' },
};

const RecipeCard = ({ r, onEdit, onDirect }) => (
  <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3 flex flex-col gap-2" data-testid={`recipe-card-${r.id}`}>
    <div className="flex justify-between items-start gap-2">
      <div>
        <p className="text-white font-semibold leading-tight">{r.name}</p>
        <p className="text-gray-500 text-xs">{r.category} · ${r.price.toFixed(2)}</p>
      </div>
      {STATUS[r.status] && r.recipe.length > 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${STATUS[r.status].cls}`} data-testid={`recipe-status-${r.id}`}>{STATUS[r.status].label}</span>
      )}
    </div>
    {r.recipe.length === 0 ? (
      <p className="text-gray-500 text-xs italic">Sin receta: no descuenta inventario</p>
    ) : (
      <ul className="text-xs text-gray-300 space-y-0.5">
        {r.recipe.map((l) => <li key={l.inventory_item_id}>• {fmtQty(l.quantity)} {l.unit} {l.name}</li>)}
      </ul>
    )}
    {r.recipe.length > 0 && (
      <div className="flex justify-between text-xs border-t border-gray-800 pt-2">
        <span className="text-gray-400">Costo: <b className="text-white" data-testid={`recipe-cost-${r.id}`}>${r.cost.toFixed(2)}</b></span>
        <span className={r.margin_percent >= 50 ? 'text-emerald-300' : 'text-amber-300'}>Margen: {r.margin_percent}%</span>
      </div>
    )}
    <div className="flex gap-2 mt-auto">
      <Button size="sm" className="flex-1 h-8 text-xs bg-emerald-700 hover:bg-emerald-600" onClick={() => onEdit(r)} data-testid={`recipe-edit-${r.id}`}>
        <BookOpen className="h-3.5 w-3.5 mr-1" /> {r.recipe.length ? 'Editar receta' : 'Crear receta'}
      </Button>
      {r.recipe.length === 0 && (
        <Button size="sm" variant="outline" className="h-8 text-xs border-sky-600/50 text-sky-300" onClick={() => onDirect(r)} title="Para refrescos y empaquetados: 1 venta = 1 pieza" data-testid={`recipe-direct-${r.id}`}>
          <Package className="h-3.5 w-3.5 mr-1" /> Por pieza
        </Button>
      )}
    </div>
  </div>
);

export const RecipesPanel = ({ onChanged }) => {
  const [recipes, setRecipes] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([inventoryApi.recipes(), inventoryApi.items()]);
      setRecipes(r); setSupplies(s);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const direct = async (r) => {
    try {
      await inventoryApi.createDirect(r.id);
      toast.success(`"${r.name}" ahora se controla por pieza. Registra su entrada en Insumos.`);
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const filtered = useMemo(() => recipes.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())), [recipes, search]);
  const withRecipe = recipes.filter((r) => r.recipe.length).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto del menú..." className="pl-9 bg-black/50 border-gray-700 text-white" data-testid="recipe-search" />
        </div>
        <span className="text-gray-400 text-sm" data-testid="recipe-coverage">{withRecipe} de {recipes.length} productos con receta</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map((r) => <RecipeCard key={r.id} r={r} onEdit={setEditing} onDirect={direct} />)}
      </div>
      {editing && <RecipeDialog product={editing} supplies={supplies} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
};
